import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, useInView, AnimatePresence, useReducedMotion } from 'framer-motion';
import { 
  ArrowRight, 
  Sparkles, 
  ChevronDown,
  Gift,
  Menu,
  X
} from 'lucide-react';
import Logo from '../components/Logo';
import Logo2 from '../components/Logo2';

// Import SVG illustrations - Using your original imports
import GiftIllustration from '../assets/undraw_gift_mtlf.svg';
// import GiftBoxIllustration from '../assets/undraw_gift-box_io08.svg';
import GiftJoyIllustration from '../assets/undraw_gift-joy_kqz4.svg';
import GiftsIllustration from '../assets/undraw_gifts_4gy3.svg';
import HoldingFlowersIllustration from '../assets/undraw_holding-flowers_jc03.svg';
import PartyIllustration from '../assets/undraw_party_k6eg.svg';
import SendGiftIllustration from '../assets/undraw_send-gift_jl1z.svg';
import PureLoveIllustration from '../assets/undraw_pure-love_cvaw.svg';
import GiftCardIllustration from '../assets/undraw_gift-card_sfy8.svg';
import ShowingSupportIllustration from '../assets/undraw_showing-support_ixfc.svg';
import ChatAIIllustration from '../assets/undraw_chat-with-ai_ir62.svg';
import OrderConfirmedIllustration from '../assets/undraw_order-confirmed_m9e9.svg';
import WishlistIllustration from '../assets/undraw_wishlist_0k5w.svg';

// Floating animation with reduced motion support
const useFloatingAnimation = () => {
  const shouldReduceMotion = useReducedMotion();
  
  return {
    initial: { y: 0, scale: 1 },
    animate: shouldReduceMotion ? {} : { 
      y: [0, -10, 0],
      transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' }
    }
  };
};

// Header Component - Enhanced mobile experience with your Logo
const GetStartedHeader = ({ onLogin, onRegister }) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 20);
      
      if (currentScrollY > lastScrollY && currentScrollY > 100 && window.innerWidth < 768) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  }, []);

  const navItems = [
    { id: 'problem', label: 'Problem' },
    { id: 'solution', label: 'Solution' },
    { id: 'how-it-works', label: 'How it Works' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ 
        y: hidden ? -100 : 0,
        backgroundColor: scrolled ? 'rgba(251, 252, 246, 0.95)' : 'rgba(251, 252, 246, 0.8)'
      }}
      transition={{ duration: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-all duration-300 ${
        scrolled ? 'border-slate-200 shadow-sm' : 'border-transparent'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo />
          
          <nav className="hidden md:flex items-center gap-1 lg:gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="px-3 py-2 text-sm lg:text-base text-slate-700 hover:text-[#06402B] font-medium transition-colors rounded-lg hover:bg-emerald-50/50"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            <button
              onClick={onLogin}
              className="px-4 lg:px-5 py-2 lg:py-2.5 text-sm lg:text-base text-slate-700 font-medium hover:text-[#06402B] transition-colors rounded-lg hover:bg-emerald-50"
            >
              Sign in
            </button>
            <button
              onClick={onRegister}
              className="px-4 lg:px-6 py-2 lg:py-2.5 text-sm lg:text-base bg-[#06402B] text-white font-medium rounded-lg hover:bg-[#053d26] transition-all shadow-sm hover:shadow-md active:scale-95 transform"
            >
              Get Started
            </button>
          </div>

          <button
            className="md:hidden inline-flex items-center justify-center p-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm active:scale-95 transition-transform"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close navigation" : "Open navigation"}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-slate-200 overflow-hidden shadow-xl"
          >
            <div className="px-4 py-6 space-y-2 max-h-[80vh] overflow-y-auto">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left text-slate-700 font-semibold py-3 px-4 text-lg rounded-xl hover:bg-emerald-50 active:bg-emerald-100 transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 flex flex-col gap-3 border-t border-slate-100 mt-4">
                <button
                  onClick={() => { onLogin(); setIsMobileMenuOpen(false); }}
                  className="w-full px-4 py-3.5 text-base text-slate-700 font-semibold border-2 border-slate-200 rounded-xl hover:border-[#06402B] hover:text-[#06402B] transition-all"
                >
                  Sign in
                </button>
                <button
                  onClick={() => { onRegister(); setIsMobileMenuOpen(false); }}
                  className="w-full px-4 py-3.5 text-base bg-[#06402B] text-white font-semibold rounded-xl shadow-lg hover:bg-[#053d26] transition-all"
                >
                  Get Started
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

// Your Original Footer - Preserved exactly as requested
const GetStartedFooter = ({ onGetStarted }) => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#06402B] border-t border-slate-200 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <Logo2 />
            <p className="text-white text-sm sm:text-base mt-4 max-w-md leading-relaxed">
              AI-powered gift recommendations that understand who you're shopping for. 
              Find thoughtful gifts in seconds, not hours.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold text-base sm:text-lg text-white mb-4">Product</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('problem')}
                  className="text-sm sm:text-base text-white hover:text-slate-200 transition-colors"
                >
                  Problem
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('solution')}
                  className="text-sm sm:text-base text-white hover:text-slate-200 transition-colors"
                >
                  Solution
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-sm sm:text-base text-white hover:text-slate-200 transition-colors"
                >
                  How it Works
                </button>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-base sm:text-lg text-white mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('faq')}
                  className="text-sm sm:text-base text-white hover:text-slate-200 transition-colors"
                >
                  FAQ
                </button>
              </li>
              <li>
                <button className="text-sm sm:text-base text-white hover:text-slate-200 transition-colors">
                  Privacy Policy
                </button>
              </li>
              <li>
                <button className="text-sm sm:text-base text-white hover:text-slate-200 transition-colors">
                  Terms of Service
                </button>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-200 pt-8">
          <div className="rounded-lg px-6 py-4 mb-6">
            <p className="text-white text-xs sm:text-sm text-center">
              As an Amazon Associate, GivAura earns from qualifying purchases.
            </p>
          </div>
          <p className="text-white text-xs sm:text-sm text-center">
            © 2026 GivAura. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Hero Section - With your SVGs and mobile adjustments
const HeroSection = ({ onGetStarted }) => {
  const floatingAnim = useFloatingAnimation();
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="relative min-h-screen bg-[#FBFCF6] pt-24 sm:pt-28 lg:pt32 pb-16 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/50 to-transparent pointer-events-none" />
      
      {/* Mobile: Hidden or smaller, Desktop: Full size */}
      <div className="absolute md:left-24 bottom-20 sm:bottom-0 w-48 sm:w-48 md:w-64 lg:w-80 h-66 sm:h-48 md:h-64 lg:h-80 opacity-100 sm:opacity-100 pointer-events-none select-none">
        <motion.img 
          src={PureLoveIllustration} 
          alt="" 
          className="w-full h-full object-contain drop-shadow-lg pointer-events-none select-none"
          draggable="false"
          animate={floatingAnim.animate}
          initial={floatingAnim.initial}
        />
      </div>

      <div className="absolute right-0 top-32 w-32 sm:w-48 md:w-64 lg:w-80 h-32 sm:h-48 md:h-64 lg:h-80 opacity-0 sm:opacity-100 pointer-events-none select-none">
        <motion.img 
          src={PartyIllustration} 
          alt="" 
          className="w-full h-full object-contain drop-shadow-lg pointer-events-none select-none"
          draggable="false"
          animate={floatingAnim.animate}
          initial={floatingAnim.initial}
          style={{ animationDelay: '1s' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-16">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full text-[#06402B] font-semibold text-sm mb-6 sm:mb-8 shadow-sm"
          >
            <Gift className="w-4 h-4" />
            <span>Gifting made easy!</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.3, delay: 0.1 }}
            className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-4 sm:mb-6 leading-[1.1] tracking-tight"
          >
            Find gifts they'll{' '}
            <span className="text-[#06402B] relative">
              actually
              <svg className="absolute w-full h-3 -bottom-1 left-0 text-emerald-200 pointer-events-none" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
              </svg>
            </span>{' '}
            love
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.3, delay: 0.2 }}
            className="text-sm sm:text-xl text-slate-600 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2"
          >
            Stop guessing. Our AI understands personalities, occasions, and relationships 
            to recommend gifts that feel personal and thoughtful.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.3, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <button
              onClick={onGetStarted}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-[#06402B] text-white font-semibold rounded-lg hover:bg-[#053d26] transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2 text-base sm:text-lg"
            >
              Get Started
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto px-8 py-4 text-slate-700 font-semibold rounded-xl hover:bg-slate-100 transition-all inline-flex items-center justify-center gap-2 text-sm"
            >
              See how it works
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Problem Section - Using your SVGs with mobile sizing
const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const shouldReduceMotion = useReducedMotion();
  const floatingAnim = useFloatingAnimation();

  const problems = [
    {
      number: "01",
      title: "Time-consuming search",
      description: "Hours spent browsing countless websites and still feeling uncertain about your choice.",
      illustration: SendGiftIllustration
    },
    {
      number: "02",
      title: "Generic suggestions",
      description: "One-size-fits-all recommendations that don't consider the recipient's unique personality.",
      illustration: GiftCardIllustration
    },
    {
      number: "03",
      title: "Decision paralysis",
      description: "Too many options without clear guidance leads to stress and last-minute panic buying.",
      illustration: GiftJoyIllustration
    }
  ];

  return (
    <section ref={ref} id="problem" className="py-16 sm:py-20 lg:py-26 px-4 sm:px-6 lg:px-8 bg-[#FBFCF6] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-emerald-50/30 to-transparent pointer-events-none" />
      
      <div className="max-w-6xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <span className="text-[#06402B] font-semibold text-sm uppercase tracking-wider mb-2 block">The Challenge</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            The problem
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
            Gift-giving shouldn't be stressful. But for most people, it is.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {problems.map((problem, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: shouldReduceMotion ? 0 : 0.4, delay: idx * 0.1 }}
              className="group bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 border border-slate-200 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-100/50 transition-all duration-300"
            >
              <div className="relative mb-6 sm:mb-8 pointer-events-none select-none">
                {/* Mobile: Smaller max width, Desktop: Larger */}
                <div className="w-full aspect-square max-w-[160px] sm:max-w-[200px] mx-auto relative">
                  <motion.img 
                    src={problem.illustration} 
                    alt={problem.title}
                    className="w-full h-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300 pointer-events-none select-none"
                    draggable="false"
                    animate={floatingAnim.animate}
                    initial={floatingAnim.initial}
                  />
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center border-2 border-emerald-100 pointer-events-auto">
                    <span className="text-[#06402B] font-bold text-sm">{problem.number}</span>
                  </div>
                </div>
              </div>
              
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 text-center group-hover:text-[#06402B] transition-colors">
                {problem.title}
              </h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed text-center">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Section - Using your SVGs
const SolutionSection = ({ onGetStarted }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [openItem, setOpenItem] = useState('connect');
  const shouldReduceMotion = useReducedMotion();

  const solutionSteps = [
    {
      id: 'connect',
      title: 'Tell us about them',
      description: 'Share details about the recipient - their interests, personality, and your relationship. Our AI creates a comprehensive profile to ensure accurate recommendations.',
      illustration: GiftIllustration
    },
    {
      id: 'ai',
      title: 'AI finds perfect matches',
      description: 'Our intelligent system analyzes thousands of products, considering personality traits, occasion context, and relationship dynamics to find gifts they\'ll genuinely love.',
      illustration: ChatAIIllustration
    },
    {
      id: 'save',
      title: 'Save and organize',
      description: 'Create wishlists, save favorites, and organize gift ideas by occasion. Never forget a birthday or anniversary again with our smart reminder system.',
      illustration: WishlistIllustration
    },
    {
      id: 'shop',
      title: 'Shop with confidence',
      description: 'Browse curated recommendations with detailed explanations of why each gift is a great match. Make informed decisions backed by AI intelligence.',
      illustration: OrderConfirmedIllustration
    }
  ];

  const currentStep = solutionSteps.find(step => step.id === openItem) || solutionSteps[0];

  return (
    <section ref={ref} id="solution" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-[#FBFCF6]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
          className="mb-10 sm:mb-16"
        >
          <span className="text-[#06402B] font-semibold text-sm uppercase tracking-wider mb-2 block">How we help</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            The solution
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl">
            Four simple steps to finding the perfect gift, every time.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="space-y-3 sm:space-y-4 order-2 lg:order-1">
            {solutionSteps.map((step, idx) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: shouldReduceMotion ? 0 : 0.3, delay: idx * 0.1 }}
              >
                <button
                  onClick={() => setOpenItem(openItem === step.id ? '' : step.id)}
                  className={`w-full text-left p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 ${
                    openItem === step.id 
                      ? 'border-[#06402B] bg-emerald-50/50 shadow-lg' 
                      : 'border-slate-200 hover:border-emerald-200 hover:bg-white hover:shadow-md'
                  }`}
                  aria-expanded={openItem === step.id}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-lg sm:text-xl font-bold ${openItem === step.id ? 'text-[#06402B]' : 'text-slate-900'}`}>
                      {step.title}
                    </h3>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      openItem === step.id ? 'bg-[#06402B] text-white rotate-180' : 'bg-slate-100 text-slate-400'
                    }`}>
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </div>
                  <AnimatePresence initial={false}>
                    {openItem === step.id && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
                        className="text-sm sm:text-base text-slate-600 leading-relaxed overflow-hidden"
                      >
                        {step.description}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            ))}
            
            <motion.button
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.4 }}
              onClick={onGetStarted}
              className="mt-6 w-full sm:w-auto px-6 py-3 bg-[#06402B] text-white font-semibold rounded-xl hover:bg-[#053d26] transition-all shadow-lg inline-flex items-center justify-center gap-2 group"
            >
              Try it now
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: shouldReduceMotion ? 0 : 0.4, delay: 0.2 }}
            className="lg:sticky lg:top-28 order-1 lg:order-2"
          >
            <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-emerald-50 to-slate-50 border border-slate-200 p-8 sm:p-12 min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] flex items-center justify-center relative overflow-hidden shadow-inner pointer-events-none select-none">
              <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #06402B 1px, transparent 0)', backgroundSize: '20px 20px' }} />
              </div>
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={openItem}
                  // Mobile: max-w-[220px], Tablet: max-w-[300px], Desktop: max-w-[400px]
                  className="w-full max-w-[220px] sm:max-w-[300px] lg:max-w-[400px]"
                  initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.9, rotate: 5 }}
                  transition={{ 
                    duration: shouldReduceMotion ? 0 : 0.3,
                    ease: [0.34, 1.56, 0.64, 1]
                  }}
                >
                  <img 
                    src={currentStep.illustration}
                    alt={currentStep.title}
                    className="w-full h-auto drop-shadow-xl pointer-events-none select-none"
                    draggable="false"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section - Using your SVGs
const HowItWorksSection = ({ onGetStarted }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const shouldReduceMotion = useReducedMotion();
  const floatingAnim = useFloatingAnimation();

  const steps = [
    {
      step: "01",
      title: "Share interests",
      description: "Tell us about the recipient their interests, your relationship, and the occasion. The more details, the better the recommendations.",
      illustration: ShowingSupportIllustration
    },
    {
      step: "02",
      title: "AI finds matches",
      description: "Our intelligent system analyzes thousands of products across hundreds of retailers to find perfect matches based on personality and preferences.",
      illustration: ChatAIIllustration
    },
    {
      step: "03",
      title: "Choose & gift",
      description: "Browse personalized recommendations with AI-generated explanations of why each gift is perfect. Purchase directly or save for later.",
      illustration: GiftsIllustration
    }
  ];

  return (
    <section ref={ref} id="how-it-works" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-[#FBFCF6]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <span className="text-[#06402B] font-semibold text-sm uppercase tracking-wider mb-2 block">Process</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            How it works
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
            From thought to thoughtful gift in three simple steps.
          </p>
        </motion.div>

        <div className="space-y-16 sm:space-y-20 lg:space-y-32">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: shouldReduceMotion ? 0 : 0.5, delay: idx * 0.15 }}
              className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16`}
            >
              <div className="w-full lg:w-1/2">
                <div className="relative group">
                  <div className="rounded-2xl sm:rounded-3xl bg-white border border-slate-200 shadow-xl p-8 sm:p-12 lg:p-16 overflow-hidden transition-transform hover:scale-[1.02] pointer-events-none select-none">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#06402B] to-emerald-400 pointer-events-none" />
                    <motion.img 
                      src={step.illustration}
                      alt={step.title}
                      // Mobile: max-w-[200px], Desktop: no limit within container
                      className="w-full max-w-[200px] sm:max-w-none mx-auto h-auto drop-shadow-lg pointer-events-none select-none"
                      draggable="false"
                      animate={floatingAnim.animate}
                      initial={floatingAnim.initial}
                    />
                  </div>
                  
                  <div className={`absolute -top-4 ${idx % 2 === 0 ? '-left-4' : '-right-4'} w-16 h-16 sm:w-20 sm:h-20 bg-[#06402B] rounded-2xl flex items-center justify-center text-white font-bold text-2xl sm:text-3xl shadow-lg pointer-events-none`}>
                    {step.step}
                  </div>

                  {idx !== steps.length - 1 && (
                    <div className="hidden lg:block absolute top-full left-1/2 transform -translate-x-1/2 w-0.5 h-16 bg-gradient-to-b from-emerald-200 to-transparent mt-8 pointer-events-none" />
                  )}
                </div>
              </div>

              <div className="w-full lg:w-1/2 text-center lg:text-left px-2 sm:px-0">
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0">
                  {step.description}
                </p>
                
                {idx === 0 && (
                  <div className="flex items-center gap-2 text-emerald-600 font-semibold justify-center lg:justify-start">
                    <Sparkles className="w-5 h-5" />
                    <span className="text-sm">Takes less than 2 minutes</span>
                  </div>
                )}
                
                {idx === steps.length - 1 && (
                  <button
                    onClick={onGetStarted}
                    className="inline-flex items-center gap-2 text-[#06402B] font-bold hover:gap-3 transition-all text-lg group"
                  >
                    Start gifting smarter
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section - Using your styling
const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [openFAQ, setOpenFAQ] = useState(null);
  const shouldReduceMotion = useReducedMotion();
  const floatingAnim = useFloatingAnimation();

  const faqs = [
    {
      question: "What is GivAura?",
      answer: "GivAura is an AI-powered gift recommendation platform that helps you find thoughtful, personalized gifts for anyone in your life. We analyze recipient details, occasions, and relationships to suggest gifts they'll genuinely love."
    },
    {
      question: "How accurate are the AI recommendations?",
      answer: "Our AI achieves over 90% recommendation accuracy by analyzing multiple factors including personality traits, interests, relationship dynamics, and occasion context. The more details you provide, the better our recommendations become."
    },
    {
      question: "Is GivAura free to use?",
      answer: "Yes! GivAura is completely free to use. We earn through affiliate commissions when you purchase recommended products, but this doesn't affect the price you pay or the quality of our recommendations."
    },
    {
      question: "Can I save gift ideas for later?",
      answer: "Absolutely! Create wishlists, save favorites, and organize gift ideas by recipient or occasion. Our platform helps you plan ahead and never miss important dates."
    }
  ];

  return (
    <section ref={ref} id="faq" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#FBFCF6] relative overflow-hidden">
      <div className="absolute left-0 bottom-10 w-32 sm:w-40 md:w-48 lg:w-56 xl:w-64 h-32 sm:h-40 md:h-48 lg:h-56 xl:h-64 opacity-0 sm:opacity-50 lg:opacity-100 pointer-events-none select-none">
        <motion.img 
          src={ShowingSupportIllustration} 
          alt="" 
          className="w-full h-full object-contain pointer-events-none select-none"
          draggable="false"
          animate={floatingAnim.animate}
        />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-4">
            Frequently asked questions
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 px-4">
            Everything you need to know about GivAura.
          </p>
        </motion.div>

        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: shouldReduceMotion ? 0 : 0.3, delay: idx * 0.1 }}
            >
              <button
                onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                className={`w-full text-left p-4 sm:p-6 rounded-xl border-2 transition-all ${
                  openFAQ === idx 
                    ? 'border-[#06402B] bg-emerald-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                aria-expanded={openFAQ === idx}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 pr-4">{faq.question}</h3>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                    openFAQ === idx ? 'rotate-180' : ''
                  }`} />
                </div>
                <AnimatePresence>
                  {openFAQ === idx && (
                    <motion.p
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      className="text-sm sm:text-base text-slate-600 leading-relaxed"
                    >
                      {faq.answer}
                    </motion.p>
                  )}
                </AnimatePresence>
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section - Using your SVG and styling approach
const FinalCTASection = ({ onGetStarted }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const shouldReduceMotion = useReducedMotion();
  const floatingAnim = useFloatingAnimation();

  return (
    <section ref={ref} className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#FBFCF6] relative overflow-hidden">
      <div className="absolute right-0 bottom-0 w-40 sm:w-48 md:w-60 lg:w-74 h-40 sm:h-48 md:h-60 lg:h-74 xl:h-80 opacity-90 sm:opacity-50 md:opacity-100 pointer-events-none select-none">
        <motion.img 
          src={HoldingFlowersIllustration} 
          alt="" 
          className="w-full h-full object-contain pointer-events-none select-none"
          draggable="false"
          animate={floatingAnim.animate}
        />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10 px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: shouldReduceMotion ? 0 : 0.4 }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4 sm:mb-6">
            Ready to find the perfect gift?
          </h2>

          <p className="text-sm sm:text-base text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto">
            Sign in to start finding thoughtful gifts today. No setup required.
          </p>

          <button
            onClick={onGetStarted}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-slate-900 font-bold rounded-lg hover:bg-slate-100 transition-all shadow-lg inline-flex items-center gap-2 text-base sm:text-lg"
          >
            Sign in
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// Main Component
const GetStartedPage = ({ onGetStarted, onLogin, onRegister }) => {
  useEffect(() => {
    window.history.pushState({ page: 'getStarted' }, '', window.location.href);
    
    const handlePopState = () => {
      window.history.pushState({ page: 'getStarted' }, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleGetStarted = () => {
    onGetStarted();
  };

  return (
    <div className="bg-white overflow-x-hidden">
      <GetStartedHeader onLogin={onLogin} onRegister={onRegister} />
      
      <HeroSection onGetStarted={handleGetStarted} />
      <ProblemSection />
      <SolutionSection onGetStarted={handleGetStarted} />
      <HowItWorksSection onGetStarted={handleGetStarted} />
      <FAQSection />
      <FinalCTASection onGetStarted={handleGetStarted} />
      
      <GetStartedFooter onGetStarted={handleGetStarted} />
    </div>
  );
};

export default GetStartedPage;